// biome-ignore-all lint/style/useNamingConvention: existing error

/* public-path Sets the cdn path of the chunks.  Must come first in this file */
import '~/common/public-path';

/* Global imports */
import {lazy, Suspense, useEffect, useMemo, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {ModalProvider} from 'react-modal-hook';
import {Provider, useDispatch, useSelector, useStore} from 'react-redux';
import {Route, Router, Switch} from 'react-router-dom';
import {ToastContainer, toast} from 'react-toastify';

/* Local imports */
import urls from 'urls';
import {apiClient} from '~/api/api-client';
import {stateToBackendEventRepresentation} from '~/CreateFabricInvitation/api/event';
import {CreateInvitationCreateDesign} from '~/CreateFabricInvitation/routes/EventCreateDesign/CreateInvitationCreateDesign';
import {InvitationMakerEventCreateDesign} from '~/CreateFabricInvitation/routes/InvitationMaker/InvitationMakerCreateDesign';
import {isInAppMode} from '~/common/checkout/utils/app';
import {ErrorBoundary} from '~/common/components/ErrorBoundary/ErrorBoundary';
import {useAppTagging} from '~/common/components/ErrorBoundary/useAppTagging';
import {fetchEventAttributes} from '~/common/event/eventAttributes/api';
import {FabricInvitationContextParent} from '~/common/FabricInvitation/context/FabricInvitationContext';
import {INIT_APP} from '~/common/fabric/constants';
import * as guestsAPI from '~/common/fabric/EventGuests/api/guests';
import {UPDATE_GUESTS} from '~/common/fabric/EventGuests/reducers/guests/constants';
import {selectEventStatus} from '~/common/fabric/reducers/eviteEvent/selectors';
import {setEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {isDraft} from '~/common/utils/event_utils';
import {isFabricWebview} from '~/common/utils/webview';
import {fetchInvitationSummary} from '~/ViewInvitation/reducers/apiActions';
import {SuspenseFallback} from '~library/molecules/SuspenseFallback';
import {InjectableFooter} from '~library/organisms/InjectableFooter';
import {getUserData} from '~library/utils/auth/utils';
import {PreviewNav} from './components/InvitationView/PreviewNav';
import {LoginModalReduxWrapper} from './components/LoginModal/LoginModalReduxWrapper';
import {NotifyGuestsOverlay} from './components/modals/NotifyGuestsOverlay';
import {NavHeader} from './components/NavHeader/NavHeader';
import {history} from './history';
import {
  getCurrentStep,
  navigateToRoute,
  setupWebviewNavigation,
  useCurrentRoute,
} from './navigation';
import {PROPAGATE_STEP_CHANGE, SET_ALL_ASSETS} from './reducers/constants';
import {eventInEditMode, eventInUpsellMode} from './reducers/details/selectors';
import {selectIsEditingDesign, setIsEditingDesign} from './reducers/preview/reducer';
import {store} from './reducers/store';
import {isInStep4RefreshVariation, isProOrPartyPassUser} from './utils/experiments';
import {useTrackPages} from './utils/trackPages';
import {
  setupWebviewCheckoutCompleteConnection,
  setupWebviewDowngradeConnection,
  setupWebviewPostLoginSuccessConnection,
  setupWebviewSendCompleteConnection,
  setupWebviewUpsellAcceptConnection,
  webViewOnNav,
  webViewPendingUpgradeToPremium,
} from './webview/webview';

import 'react-toastify/dist/ReactToastify.min.css';
import './app.sass';

const InvitationView = lazy(() =>
  import('./components/InvitationView/InvitationView').then((module) => ({
    default: module.InvitationView,
  }))
);
const EventDetails = lazy(() =>
  import('./routes/EventDetails/EventDetails').then((module) => ({default: module.EventDetails}))
);
const EventGiftingWrapper = lazy(() =>
  import('./routes/EventGifting/EventGiftingWrapper').then((module) => ({
    default: module.EventGiftingWrapper,
  }))
);
const EventPreview = lazy(() =>
  import('./routes/Preview/Preview').then((module) => ({
    default: module.Preview,
  }))
);
const EventGuests = lazy(() =>
  import('~/common/fabric/EventGuests/EventGuests').then((module) => ({
    default: module.EventGuests,
  }))
);
const Success = lazy(() =>
  import('./routes/Success/Success').then((module) => ({default: module.Success}))
);
const Upsell = lazy(() =>
  import('./routes/Upsell/Upsell').then((module) => ({default: module.Upsell}))
);

const GuestOptionsOverlay = lazy(() =>
  import('~/common/fabric/EventGuests/components/GuestOptions/GuestOptionsOverlay').then(
    (module) => ({
      default: module.GuestOptionsOverlay,
    })
  )
);

const EventReview = lazy(() =>
  import('~/common/fabric/routes/EventReview/EventReview').then((module) => ({
    default: module.EventReview,
  }))
);

const isInWebview = isFabricWebview();

const currentUrl = new window.URL(window.location.href);
store.dispatch({
  type: INIT_APP,
  payload: {
    event: window.evite_event,
    template: window.fabricTemplate,
    stamps: [],
    stickers: [],
    liners: [],
    envelopes: [],
    backgrounds: [],
    user: getUserData(isInWebview),
    category_event_type: window.category_event_type,
    category_id: new URLSearchParams(window.location.search).get('c'),
    override_freemium:
      !(isInWebview || isProOrPartyPassUser(window?.subscriptionPlanName)) &&
      currentUrl.searchParams.get('variant') === 'premium',
    widgets: {...window.event_widgets},
  },
});

if (window.fabricTemplate?.premium) {
  webViewPendingUpgradeToPremium(false);
}

if (window.evite_event) {
  store.dispatch(fetchInvitationSummary(window.evite_event.id));
  store.dispatch(guestsAPI.fetchGuestsListThunk()); // duplicated because the AddGuests ABTest
}

const {id: currentStep} = getCurrentStep({store});

if (currentStep >= 5) {
  const {id: eventId} = window.evite_event;

  store.dispatch({type: UPDATE_GUESTS, payload: {eventId}});
  store.dispatch(guestsAPI.fetchContactsThunk());
  store.dispatch(guestsAPI.fetchGuestsListThunk());
  store.dispatch(guestsAPI.fetchGuestsImportThunk());
}

setupWebviewNavigation(store);
setupWebviewDowngradeConnection(store);
setupWebviewCheckoutCompleteConnection(store);
setupWebviewSendCompleteConnection(store);
setupWebviewPostLoginSuccessConnection(store, getCurrentStep, navigateToRoute);
setupWebviewUpsellAcceptConnection(store);

const TrackRouteChanges = ({contentPanel}) => {
  const pathObj = useCurrentRoute();
  const sections = document.getElementsByClassName('create-flow__content-section');

  for (let i = 0; i < sections.length; i++) {
    sections[i].classList.remove('active');
  }

  const activeSection = document.getElementById(pathObj.name);
  if (activeSection && contentPanel?.current) {
    activeSection.classList.add('active');
    contentPanel.current.scrollTop = activeSection.offsetTop - 120;
  }

  // Report navigation status back the web apps
  // We do it here to support browsers forward and back buttons as well as in app navigation
  webViewOnNav(pathObj.name, pathObj.nextRoute, pathObj.previousRoute, pathObj.nextButtonText);

  // Track GA page views
  useTrackPages();

  return null;
};

const FabricCreateInvitationApp = () => {
  const store = useStore();
  const contentPanel = useRef(null);
  const {path, hideInWebView, showFooter} = useCurrentRoute();
  const inEditMode = useSelector(eventInEditMode);
  const isEditingDesign = useSelector(selectIsEditingDesign);
  const eventStatus = useSelector(selectEventStatus);
  const inAppMode = isInAppMode();
  const inUpsellMode = useSelector(eventInUpsellMode);
  const dispatch = useDispatch();

  const [inStep4RefreshVariation, setInStep4RefreshVariation] = useState(false);

  const isDesktop = useIsLargishAndUp();
  useEffect(() => {
    setInStep4RefreshVariation(
      isInStep4RefreshVariation(
        isDesktop,
        path !== 'preview' || !isDraft(eventStatus) // Only bucket if on create flow step 4
      )
    );
  }, [isDesktop, path, eventStatus]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger when path changes
  useEffect(() => {
    dispatch(setIsEditingDesign(false));
  }, [path]);

  const CONTENT_PATHS = ['/:template/details', '/:template/gifting'];
  if (inStep4RefreshVariation) CONTENT_PATHS.push('/:template/preview', '/:template/edit');

  useAppTagging({
    eventId: window.evite_event?.id,
    inAppMode: inAppMode,
    inEditMode: inEditMode,
  });

  useEffect(() => {
    if (
      ['create', 'customize', 'details', 'gifting', 'preview', 'guests', 'review'].includes(path) &&
      !inUpsellMode &&
      !inAppMode
    ) {
      document.body.classList.add('no-overflow');
    }

    dispatch({type: PROPAGATE_STEP_CHANGE});

    return () => {
      window.scroll(0, 0);
      document.body.classList.remove('no-overflow');
    };
  }, [path, inUpsellMode, inAppMode, dispatch]);

  useEffect(() => {
    const appHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    window.addEventListener('resize', appHeight);

    appHeight();

    return () => {
      window.removeEventListener('resize', appHeight);
    };
  }, []);

  const sending = path === 'send';

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing warning around missing deps
  useEffect(() => {
    const fetchAllAssets = async () => {
      const url = urls.get('ajax_get_all_assets', {
        asset_groups: window.fabricTemplate.asset_groups,
        primary_categories: window.fabricTemplate.primary_categories,
        template: window.fabricTemplate.id,
      });
      const resp = await apiClient.get(url);
      const response = await resp.json();
      const {
        stamps: allStamps,
        stickers: allStickers,
        liners: allLiners,
        backgrounds: allBackgrounds,
        overlays: allOverlays,
        reveals: allReveals,
      } = response;
      const allEnvelopes = response.envelopes
        ? response.envelopes.filter(({blank}) => !!blank)
        : [];
      dispatch({
        type: SET_ALL_ASSETS,
        payload: {
          allAssets: {
            allStamps,
            allStickers,
            allLiners,
            allBackgrounds,
            allEnvelopes,
            allOverlays,
            allReveals,
          },
        },
      });
    };
    fetchAllAssets().then(() => {
      store.dispatch(
        setEventToBackendSnapshot(stateToBackendEventRepresentation(store.getState()))
      );
    });
  }, []);

  useEffect(() => {
    fetchEventAttributes(store).catch((e) => evite.error('Failed to fetch event attributes', e));
  }, [store]);

  const renderGuestOptions = () => <GuestOptionsOverlay fullScreen />;

  const NavElement = useMemo(() => {
    if ((path === 'preview' && inEditMode) || sending) return null;
    if (inStep4RefreshVariation && isEditingDesign) return <PreviewNav />;
    return <NavHeader />;
  }, [path, inEditMode, sending, inStep4RefreshVariation, isEditingDesign]);

  return (
    <FabricInvitationContextParent>
      <TrackRouteChanges contentPanel={contentPanel} />
      {NavElement}
      <div className={`root-page-wrapper ${path}`}>
        <div className="create-flow__page" id={path}>
          {(!hideInWebView || !isInWebview) && (
            <Suspense fallback={<SuspenseFallback />}>
              <Switch>
                <Route path={CONTENT_PATHS}>
                  <InvitationView contentPanel={contentPanel} />
                </Route>
              </Switch>
              <Switch>
                <Route
                  exact
                  path={['/:template/create', '/:eventId/customize']}
                  component={CreateInvitationCreateDesign}
                />
                <Route exact path={['/:eventId/details']} component={EventDetails} />
                <Route exact path={['/:eventId/gifting']} component={EventGiftingWrapper} />
                <Route
                  exact
                  path={['/:eventId/preview', '/:eventId/edit']}
                  component={
                    inStep4RefreshVariation ? EventPreview : InvitationMakerEventCreateDesign
                  }
                />
                <Route exact path="/:eventId/guests" component={EventGuests} />
                <Route exact path="/:eventId/guest-options" component={renderGuestOptions} />
                <Route exact path="/:eventId/review" component={EventReview} />
                <Route exact path="/:eventId/send" component={Upsell} />
                <Route exact path="/:eventId/confirm" component={Success} />
              </Switch>
            </Suspense>
          )}
        </div>
      </div>
      {showFooter && <InjectableFooter />}
    </FabricInvitationContextParent>
  );
};

const FabricCreateInvitationAppWrapper = () => (
  <ErrorBoundary componentName="FabricCreateInvitationAppWrapper">
    <Provider store={store}>
      <ModalProvider>
        <Router history={history}>
          <LoginModalReduxWrapper />
          <FabricCreateInvitationApp />
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={6000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          icon={false}
        />
        <NotifyGuestsOverlay />
      </ModalProvider>
    </Provider>
  </ErrorBoundary>
);

window.toast = toast; // TODO: Remove if toasts irrelevant here.
const root = createRoot(document.getElementById('fabric-create-invitation-root'));
root.render(<FabricCreateInvitationAppWrapper />);
